import React, { useState } from "react";
import { mockCreator } from "../../../../data/mockData";
import Card from "../../../../components/ui/Card";
import Button from "../../../../components/ui/Button";
import { PlusCircle, Check, X } from "lucide-react";
import CreateExperienceModal from "../modals/CreateExperienceModal";
// ✨ 1. IMPORT THE RECENT ACTIVITY LIST
import RecentActivityList from "../../../../components/ui/RecentActivityList";

const StatusBadge = ({ status }) => {
  const statusClasses = {
    "New Request": "bg-blue-100 text-blue-700",
    Completed: "bg-green-100 text-green-700",
  };
  return (
    <span
      className={`px-2 py-1 text-xs font-semibold rounded-full ${statusClasses[status]}`}
    >
      {status}
    </span>
  );
};

const ExperiencesTab = ({
  experiences: initialExperiences,
  onCreate,
  onEdit,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [experienceToEdit, setExperienceToEdit] = useState(null);

  const { experienceRequests, mockTransactions } = mockCreator;

  // --- Stats Calculations ---
  const completedRequests = experienceRequests.filter(
    (req) => req.status === "Completed"
  );
  const pendingRequestsCount = experienceRequests.filter(
    (req) => req.status === "New Request"
  ).length;
  const totalRevenue = completedRequests.reduce((acc, req) => {
    const offeredExp = initialExperiences.find(
      (exp) => exp.title === req.experienceType
    );
    return acc + (offeredExp ? offeredExp.priceETB : 0);
  }, 0);

  // ✨ 2. FILTER FOR EXPERIENCE TRANSACTIONS
  const experienceTransactions = mockTransactions
    .filter((txn) => txn.type === "Experience")
    .slice(0, 5);

  const handleCreateClick = () => {
    setExperienceToEdit(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (experience) => {
    setExperienceToEdit(experience);
    setIsModalOpen(true);
  };

  return (
    <>
      <div className="space-y-6">
        {/* --- Analytics Cards --- */}
        <div className="grid grid-cols-1 @md:grid-cols-3 gap-4">
          <Card>
            <p className="text-sm text-slate-500">Total Experience Revenue</p>
            <p className="text-2xl font-bold text-slate-800">
              {totalRevenue.toLocaleString()} ETB
            </p>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Completed Requests</p>
            <p className="text-2xl font-bold text-slate-800">
              {completedRequests.length}
            </p>
          </Card>
          <Card>
            <p className="text-sm text-slate-500">Pending Requests</p>
            <p className="text-2xl font-bold text-slate-800">
              {pendingRequestsCount}
            </p>
          </Card>
        </div>

        {/* --- Management Sections --- */}
        <Card>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Your Offered Experiences
            </h3>
            <Button variant="primary" size="sm" onClick={onCreate}>
              <PlusCircle size={16} className="mr-2" /> Add Experience
            </Button>
          </div>
          <div className="space-y-3 grid grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3 gap-4 space-y-0">
            {initialExperiences.map((exp) => (
              <div
                key={exp.id}
                className="p-3 border border-slate-200 rounded-lg flex flex-col justify-between h-full"
              >
                <div className="mb-2">
                  <p className="font-medium text-slate-800 line-clamp-1" title={exp.title}>{exp.title}</p>
                  <p className="font-semibold text-primary text-sm">
                    {exp.priceETB} ETB
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => onEdit(exp)} className="w-full mt-auto">
                  Edit
                </Button>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h3 className="text-lg font-semibold text-slate-800 mb-4">
            Incoming Requests
          </h3>
          <div className="grid grid-cols-1 @md:grid-cols-2 gap-4">
            {experienceRequests.map((req) => (
              <div
                key={req.id}
                className="p-4 border border-slate-200 rounded-lg flex flex-col h-full"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold text-slate-800 line-clamp-1" title={req.experienceType}>
                      {req.experienceType}
                    </p>
                    <p className="text-sm text-slate-500">
                      From: {req.fanName} <br/> on {req.requestDate}
                    </p>
                  </div>
                  <StatusBadge status={req.status} />
                </div>
                {req.status === "New Request" && (
                  <div className="flex justify-end space-x-2 mt-auto pt-3">
                    <Button variant="ghost" size="sm" className="flex-1">
                      <X size={14} className="mr-1" /> Decline
                    </Button>
                    <Button variant="secondary" size="sm" className="flex-1">
                      <Check size={14} className="mr-1" /> Accept
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* ✨ 3. RENDER THE RECENT ACTIVITY LIST ✨ */}
        <RecentActivityList
          title="Recent Experience Activity"
          transactions={experienceTransactions}
        />
      </div>
    </>
  );
};

export default ExperiencesTab;
